import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { ApiError, loginPassword, loginToken } from '../api/client';
import { useAuth } from '../auth/store';
import { useT } from '../i18n';

export default function Login() {
  const t = useT();
  const auth = useAuth((s) => s.auth);
  const setAuth = useAuth((s) => s.setAuth);
  const nav = useNavigate();
  const loc = useLocation();
  const [tab, setTab] = useState<'password' | 'token'>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const from = (loc.state as { from?: string } | null)?.from ?? '/fleet';

  if (auth) return <Navigate to={from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const a = tab === 'password' ? await loginPassword(username.trim(), password) : await loginToken(token.trim());
      setAuth(a);
      nav(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 400 || err.status === 403)) setError(tab === 'password' ? t('login.badCredentials') : t('login.badToken'));
      else setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login" onSubmit={submit}>
        <div className="row" style={{ gap: 12 }}>
          <div className="logo" aria-hidden>
            C
          </div>
          <div>
            <h1>{t('app.name')}</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--muted)' }}>{t('app.tagline')}</p>
          </div>
        </div>
        <div className="seg sm" role="tablist" aria-label={t('login.method')} style={{ alignSelf: 'flex-start' }}>
          <button type="button" role="tab" aria-selected={tab === 'password'} onClick={() => setTab('password')}>
            {t('login.tabPassword')}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'token'} onClick={() => setTab('token')}>
            {t('login.tabToken')}
          </button>
        </div>
        {tab === 'password' ? (
          <>
            <label className="field">
              {t('login.username')}
              <input className="input" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
            </label>
            <label className="field">
              {t('login.password')}
              <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
          </>
        ) : (
          <>
            <label className="field">
              {t('login.token')}
              <input className="input" type="password" placeholder={t('login.tokenPlaceholder')} value={token} onChange={(e) => setToken(e.target.value)} required autoFocus />
            </label>
            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--muted)' }}>{t('login.tokenHint')}</p>
          </>
        )}
        {error && (
          <p className="error-text" role="alert" style={{ margin: 0 }}>
            {error}
          </p>
        )}
        <button type="submit" className="btn primary lg" disabled={busy}>
          {busy ? t('login.signingIn') : t('login.signIn')}
        </button>
      </form>
    </div>
  );
}
